import React from 'react'

type RenderItemInfo<ItemT> = {
  item: ItemT
  index: number
  activeIndex: number
  height: number
}

type Props<ItemT> = {
  items: ItemT[]
  renderItem: (info: RenderItemInfo<ItemT>) => React.ReactNode
  headerHeight?: number
  getKey?: (item: ItemT, index: number) => string
  className?: string
  style?: React.CSSProperties
  onActiveIndexChange?: (index: number) => void
  footer?: React.ReactNode
}

const isDev =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : process.env.NODE_ENV !== 'production'
const ACTIVE_THRESHOLD = 0.8
const DEBUG_UI_FLAG = 'tellus_debug_ui'

function isLayoutDebugEnabled() {
  if (!isDev || typeof window === 'undefined' || !window.localStorage)
    return false
  try {
    return window.localStorage.getItem(DEBUG_UI_FLAG) === '1'
  } catch {
    return false
  }
}

function layoutDebug(event: string, payload?: unknown) {
  if (!isLayoutDebugEnabled()) return
  if (payload !== undefined) {
    console.debug(`[ClipsPager] ${event}`, payload)
  } else {
    console.debug(`[ClipsPager] ${event}`)
  }
}

function setViewportHeightVar() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const height = window.visualViewport?.height ?? window.innerHeight
  document.documentElement.style.setProperty('--vh', `${height * 0.01}px`)
}

export default function ClipsPager<ItemT>({
  items,
  renderItem,
  headerHeight,
  getKey,
  className,
  style,
  onActiveIndexChange,
  footer,
}: Props<ItemT>) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [itemHeight, setItemHeight] = React.useState(0)
  const activeIndexRef = React.useRef(0)
  const itemHeightRef = React.useRef(0)

  const updateActiveIndex = React.useCallback(
    (nextIndex: number, reason: string) => {
      if (nextIndex < 0 || nextIndex === activeIndexRef.current) return
      activeIndexRef.current = nextIndex
      setActiveIndex(nextIndex)
      onActiveIndexChange?.(nextIndex)
      if (isDev) {
        console.debug('[ClipsPager] activeIndex', {nextIndex, reason})
      }
    },
    [onActiveIndexChange],
  )

  React.useLayoutEffect(() => {
    setViewportHeightVar()
    if (typeof window === 'undefined') return
    const onResize = () => {
      setViewportHeightVar()
    }
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    window.visualViewport?.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
      window.visualViewport?.removeEventListener('resize', onResize)
    }
  }, [])

  React.useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    const update = (height: number) => {
      if (!height) {
        if (typeof document !== 'undefined') {
          const rootStyles = getComputedStyle(document.documentElement)
          layoutDebug('container height is 0', {
            header: rootStyles.getPropertyValue('--header-h').trim(),
            tabbar: rootStyles.getPropertyValue('--tabbar-h').trim(),
            vh: rootStyles.getPropertyValue('--vh').trim(),
          })
        } else {
          layoutDebug('container height is 0')
        }
        return
      }
      if (Math.abs(height - itemHeightRef.current) < 1) return
      itemHeightRef.current = height
      setItemHeight(height)
      layoutDebug('item height updated', {height})
    }

    update(container.getBoundingClientRect().height)

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(entries => {
        const nextHeight = entries[0]?.contentRect?.height ?? 0
        update(nextHeight)
      })
      observer.observe(container)
      return () => observer.disconnect()
    }

    if (typeof window === 'undefined') return
    const onResize = () => {
      update(container.getBoundingClientRect().height)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [headerHeight])

  React.useEffect(() => {
    if (!items.length) {
      updateActiveIndex(0, 'reset')
    } else if (activeIndexRef.current >= items.length) {
      updateActiveIndex(0, 'reset')
    }
  }, [items.length, updateActiveIndex])

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (typeof IntersectionObserver === 'undefined') return

    const visibleMap = new Map<Element, number>()
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          visibleMap.set(entry.target, entry.intersectionRatio)
        })
        let maxRatio = 0
        let nextIndex = -1
        visibleMap.forEach((ratio, element) => {
          const idxRaw = (element as HTMLElement).dataset.clipIndex
          const idx = idxRaw ? Number(idxRaw) : -1
          if (
            !Number.isNaN(idx) &&
            ratio >= ACTIVE_THRESHOLD &&
            ratio >= maxRatio
          ) {
            maxRatio = ratio
            nextIndex = idx
          }
        })
        if (nextIndex >= 0) {
          updateActiveIndex(nextIndex, 'intersection')
        }
      },
      {
        root: container,
        threshold: [0, ACTIVE_THRESHOLD, 1],
      },
    )

    const elements =
      container.querySelectorAll<HTMLElement>('[data-clip-index]')
    elements.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [items.length, updateActiveIndex])

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return
    if (typeof IntersectionObserver !== 'undefined') return

    const onScroll = () => {
      const height = itemHeightRef.current || container.clientHeight || 1
      const nextIndex = Math.round(container.scrollTop / height)
      updateActiveIndex(nextIndex, 'scroll')
    }

    container.addEventListener('scroll', onScroll, {passive: true})
    onScroll()
    return () => container.removeEventListener('scroll', onScroll)
  }, [updateActiveIndex])

  const resolvedStyle: React.CSSProperties = {...style}
  if (headerHeight !== undefined) {
    resolvedStyle.height = `calc((var(--vh, 1vh) * 100) - ${headerHeight}px)`
    resolvedStyle.minHeight = resolvedStyle.height
  }

  const itemStyle: React.CSSProperties | undefined =
    itemHeight > 0 ? {height: itemHeight} : undefined

  return (
    <div
      ref={containerRef}
      className={className}
      style={resolvedStyle}
      data-feed-scroll="">
      {items.map((item, index) => {
        const key = getKey?.(item, index) ?? String(index)
        return (
          <div
            key={key}
            className="clipItem reel-slide"
            data-clip-item="true"
            data-clip-index={index}
            style={itemStyle}>
            {renderItem({item, index, activeIndex, height: itemHeight})}
          </div>
        )
      })}
      {footer ? (
        <div className="clipItem reel-slide load-more-slide" style={itemStyle}>
          {footer}
        </div>
      ) : null}
    </div>
  )
}
